import { FacilityCluster } from "@lib/utils/types/FacilityCluster";
import {
  calculateCoordinatesOfNearestPoint,
  calculatePathDistanceToCoordinates,
  mapFacilityToClusterPoint,
} from "@repo/map-helpers";
import { FacilitySummaryResponse, PathResponse } from "@repo/shared-types";
import {
  BBox,
  Feature,
  GeoJsonProperties,
  MultiPoint,
  Point,
  Position,
} from "geojson";
import throttle from "lodash.throttle";
import { LngLat } from "mapbox-gl";
import { useEffect, useMemo, useState } from "react";
import Supercluster from "supercluster";
import useSupercluster from "use-supercluster";

import {
  DISTANCE_CALCULATION_TIME_THRESHOLD,
  FACILITY_CLUSTER_OPTIONS,
} from "./consts";

export function useClusters(
  facilities: FacilitySummaryResponse[],
  mapBounds: BBox | undefined
) {
  const [zoom, setZoom] = useState(7);
  const existingCoordinates = new Set<string>();

  const points: Array<
    Supercluster.PointFeature<
      GeoJsonProperties & (FacilityCluster | FacilitySummaryResponse)
    >
  > = facilities.map(facility =>
    mapFacilityToClusterPoint(facility, existingCoordinates)
  );

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: mapBounds,
    zoom,
    options: FACILITY_CLUSTER_OPTIONS,
  });

  return [clusters, supercluster, zoom, setZoom] as const;
}

export function usePathPoints(path?: PathResponse) {
  if (!path) return null;

  const { coordinates } = path.geoJson.features[0].geometry;

  const start = coordinates[0];
  const end = coordinates[coordinates.length - 1];

  return {
    type: "Feature",
    geometry: {
      type: "MultiPoint",
      coordinates: [start, end],
    },
  } as Feature<MultiPoint>;
}

export function useDistanceCalculation() {
  const [distances, setDistances] = useState<{ start: number; end: number }>();
  const [pointPosition, setPointPosition] = useState<Position | undefined>(
    undefined
  );
  let ticking = false;

  const throttledDistanceCalculation = useMemo(
    () =>
      throttle(
        (selectedPath: PathResponse, pointCoords: LngLat) => {
          if (!pointCoords) return;

          const result = calculatePathDistanceToCoordinates(
            selectedPath,
            pointCoords
          );

          setDistances({
            start: result.distanceFromStart,
            end: result.distanceFromEnd,
          });
        },
        DISTANCE_CALCULATION_TIME_THRESHOLD,
        { trailing: true }
      ),
    []
  );

  const calculateDistances = (
    hoverCoords: LngLat,
    selectedPath: PathResponse,
    layerId?: string
  ) => {
    if (layerId && selectedPath.id === layerId) {
      if (!ticking) {
        requestAnimationFrame(() => {
          ticking = false;

          const position = calculateCoordinatesOfNearestPoint(
            selectedPath,
            hoverCoords
          );
          const pointCoords = new LngLat(position[0], position[1]);

          setPointPosition(position);

          throttledDistanceCalculation(selectedPath, pointCoords);
        });
      }
      ticking = true;
    } else {
      setPointPosition(undefined);
    }
  };

  const pointSource =
    pointPosition &&
    ({
      type: "Point",
      coordinates: pointPosition,
    } as Point);

  useEffect(() => {
    () => throttledDistanceCalculation.flush();
  }, [throttledDistanceCalculation]);

  return { distances, pointPosition, pointSource, calculateDistances };
}
